import { KilowattConfig, MeterConsumption, NonResidentialTariff, ResidentialTariff } from '@/types/tarrifs'
import { MeterReading } from '@/types/transactions'
import moment from 'moment'

export const KILO_WATT_CONFIG: KilowattConfig = {
    nonResidentialConfig: {
        serviceCharge: 2.13,
        serviceCharge1: 0,
        energyCharge: 2.0473,
        energyCharge1: 1.3714,
        energyCharge2: 1.2888,
        nationalElectricLevyCharge: 0.02,
        nhilGetFundLevyCharge: 0.05,
        vatLevyCharge: 15,
        streetLightCharge: 0.03,
        refundCharge: 0,
    },
    residentialConfig: {
        serviceCharge: 10.7034,
        serviceCharge1: 2.13,
        energyCharge: 1.9751,
        energyCharge1: 1.7776,
        energyCharge2: 1.3697,
        nationalElectricLevyCharge: 0.02,
        nhilGetFundLevyCharge: 0,
        vatLevyCharge: 0,
        streetLightCharge: 0.03,
        refundCharge: 0.7614,
    },
}

export class TarrifCalculatorService {
    kilowattConfig: KilowattConfig

    constructor(kilowattConfig: KilowattConfig) {
        this.kilowattConfig = kilowattConfig
    }

    public estimateDailyConsumption(params: {
        meterReadings: MeterReading[]
        isResidential: boolean
    }): MeterConsumption | null {
        const { meterReadings, isResidential } = params

        let cleanReadings = meterReadings.filter(
            element => element?.readingDate != null && element.consumptionValue != null,
        )

        if (!cleanReadings.length) return null

        // sort meter readings by date
        cleanReadings = cleanReadings.sort((a, b) => {
            const dateA = new Date(a.readingDate ?? '')
            const dateB = new Date(b.readingDate ?? '')

            return dateA.getTime() - dateB.getTime()
        })

        const latestReading = cleanReadings.at(-1) as MeterReading

        // get the total number of days in the duration
        const totalNumberOfDaysInDuration = latestReading.readingDate ? moment(latestReading.readingDate).days() : 30

        const averageConsumptionForPeriod = (latestReading.consumptionValue ?? 0) / totalNumberOfDaysInDuration

        const averageMoneySpentForPeriod = isResidential
            ? this.calculateResidentialTariff(averageConsumptionForPeriod).totalCharge
            : this.calculateNonResidentialTariff(averageConsumptionForPeriod).totalCharge

        return {
            date: latestReading.readingDate ?? moment().format('YYYY-MM-DD:HH:mm:ss'),
            kilowattHour: averageConsumptionForPeriod,
            money: averageMoneySpentForPeriod,
            cumulativeKilowattHour: averageConsumptionForPeriod,
            cumulativeMoney: averageMoneySpentForPeriod,
        }
    }

    calculateNonResidentialTariff(kwhr: number): NonResidentialTariff {
        const energyCharge = this.calculateEnergyCharge(kwhr, false)

        const streetLightCharge = this.calculateStreetLightCharge(energyCharge, false)

        const nationalElectricLevy = this.calculateNationalElectricLevy(energyCharge, false)

        const serviceCharge = this.kilowattConfig.nonResidentialConfig.serviceCharge

        const vat = (energyCharge + serviceCharge) * (this.kilowattConfig.nonResidentialConfig.vatLevyCharge / 100)

        const getFundTax =
            (energyCharge + serviceCharge) * this.kilowattConfig.nonResidentialConfig.nhilGetFundLevyCharge

        const totalCharge = energyCharge + streetLightCharge + nationalElectricLevy + serviceCharge + getFundTax + vat

        return {
            energyCharge,
            streetLightCharge,
            nationalElectricLevy,
            serviceCharge,
            getFundTax,
            vat,
            totalCharge,
        }
    }

    calculateResidentialTariff(kwhr: number): ResidentialTariff {
        const energyCharge = this.calculateEnergyCharge(kwhr)
        const streetLightCharge = this.calculateStreetLightCharge(energyCharge)
        const nationalElectricLevy = this.calculateNationalElectricLevy(energyCharge)
        const serviceCharge = this.calculateServiceCharge(kwhr)
        const refund = this.calculateTotalRefund(kwhr)
        const totalThisMonth = streetLightCharge + energyCharge + nationalElectricLevy + serviceCharge
        const netTotalCharge = totalThisMonth - refund

        return {
            energyCharge,
            streetLightCharge: streetLightCharge,
            nationalElectricLevy: nationalElectricLevy,
            serviceCharge: serviceCharge,
            refund: refund,
            totalCharge: totalThisMonth,
            netTotalCharge: netTotalCharge,
        }
    }

    calculateEnergyCharge(kwhr: number, isResidential = true): number {
        const data = isResidential ? this.kilowattConfig.residentialConfig : this.kilowattConfig.nonResidentialConfig
        if (kwhr > 600) {
            return parseFloat(
                ((kwhr - 600) * data.energyCharge + (300 * data.energyCharge1 + 300 * data.energyCharge2)).toFixed(2),
            )
        } else if (kwhr > 300) {
            return parseFloat(((kwhr - 300) * data.energyCharge1 + 300 * data.energyCharge2).toFixed(2))
        } else if (kwhr > 30) {
            return parseFloat((kwhr * data.energyCharge2).toFixed(2))
        } else {
            return parseFloat((kwhr * data.energyCharge2).toFixed(2))
        }
    }

    calculateStreetLightCharge(energyCharge: number, isResidential = true): number {
        const data = isResidential ? this.kilowattConfig.residentialConfig : this.kilowattConfig.nonResidentialConfig

        const streetLightCharge = parseFloat((energyCharge * data.streetLightCharge).toFixed(2))
        return streetLightCharge
    }

    calculateNationalElectricLevy(energyCharge: number, isResidential = true): number {
        const data = isResidential ? this.kilowattConfig.residentialConfig : this.kilowattConfig.nonResidentialConfig

        const nationalElectricLevy = parseFloat((energyCharge * data.nationalElectricLevyCharge).toFixed(2))
        return nationalElectricLevy
    }

    calculateServiceCharge(kwhr: number): number {
        const data = this.kilowattConfig.residentialConfig
        const serviceCharge = parseFloat((kwhr > 30 ? data.serviceCharge : data.serviceCharge1).toFixed(2))
        return serviceCharge
    }

    calculateTotalRefund(kwhr: number): number {
        const data = this.kilowattConfig.residentialConfig
        const totalRefund = kwhr <= 30 ? parseFloat((kwhr * data.refundCharge).toFixed(2)) : 0
        return totalRefund
    }
}
